import React from "react";

import Tab from "@mui/material/Tab";
import MuiTabs, { TabsProps } from "@mui/material/Tabs";
import styled from "styled-components";

import { color } from "../../tokens";
import { Icon } from "../Icon/Icon";
import { Tooltip } from "../Tooltip/Tooltip";
import { BasicTab } from "../types";

interface Props extends Omit<TabsProps, "children" | "onChange"> {
  tabs: BasicTab[];
  variant?: "scrollable" | "fullWidth" | "standard";
  activeTabIndex?: number;
  onTabChange?: (index: number) => void;
  children?: React.ReactNode;
}

export const BasicTabs: React.FC<Props> = (props) => {
  const {
    tabs,
    activeTabIndex,
    onTabChange,
    variant = "scrollable",
    children,
    ...rest
  } = props;

  const activeTab =
    activeTabIndex !== undefined ? tabs[activeTabIndex] : undefined;
  const activeTabColor =
    typeof activeTab?.customBorderColor === "string"
      ? activeTab.customBorderColor
      : undefined;
  const tabIndicatorProps = activeTabColor
    ? { style: { backgroundColor: activeTabColor } }
    : undefined;

  const getTabIcon = (tab: BasicTab) => {
    if (tab.hasError) {
      return (
        <Tooltip title={tab.errorTooltip ?? ""} placement="top">
          <span>
            <Icon icon="circle" size="2xs" color={color.red[400]} />
          </span>
        </Tooltip>
      );
    }
    if (tab.hasWarning) {
      return (
        <Tooltip title={tab.warningTooltip ?? ""} placement="top">
          <span>
            <Icon icon="circle" size="2xs" color={color.yellow[300]} />
          </span>
        </Tooltip>
      );
    }
    return undefined;
  };

  return (
    <>
      <StyledMuiTabs
        {...rest}
        value={activeTabIndex}
        onChange={(_, newValue: number) => {
          if (onTabChange && !tabs[newValue]?.disabled) {
            onTabChange(newValue);
          }
        }}
        variant={variant}
        scrollButtons="auto"
        TabIndicatorProps={tabIndicatorProps}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.label}
            label={
              <Tooltip
                title={
                  tab.disabled
                    ? tab.disabledTooltip ?? ""
                    : tab.tooltipValue ?? ""
                }
                placement="bottom"
              >
                <span>{tab.label}</span>
              </Tooltip>
            }
            icon={getTabIcon(tab)}
            iconPosition="end"
            aria-disabled={tab.disabled || undefined}
            sx={{
              textTransform: "none",
              paddingX: 2,
              minHeight: "3rem",
              minWidth: 0,
              ...(tab.disabled && {
                opacity: 0.5,
                cursor: "not-allowed",
                "&:hover": { opacity: 0.5 },
              }),
            }}
          />
        ))}
        {children}
      </StyledMuiTabs>

      <div key={activeTabIndex}>
        {activeTabIndex !== undefined &&
          tabs[activeTabIndex]?.render &&
          tabs[activeTabIndex]?.render()}
      </div>
    </>
  );
};

const StyledMuiTabs = styled(MuiTabs)`
  margin-top: 1.5rem;
  border-bottom: 1px solid ${color.neutral[300]};
`;
